import { asyncScheduler, expand, iif, mapTo, Observable, of, SchedulerLike, throwError, timer } from 'rxjs'
import { IntervalMaxEmissionsReached } from './IntervalMaxEmissionsReached'

/**
 * Similar to `rxjs/interval with an optional configuration to specify a delay between emissions,
 * or maximum number of iterations.
 *
 * Adapted from `https://github.com/alex-okrushko/backoff-rxjs/blob/master/src/observable/intervalBackoff.ts`
 *
 * @param config An optional `IntervalBackoffConfig` to define the frequency and number of emissions
 * @param scheduler The `SchedulerLike` implementation (e.g: AsyncScheduler, TestScheduler, etc.)
 */
export function intervalBackoff(
  config: IntervalBackoffConfig,
  scheduler: SchedulerLike = asyncScheduler,
): Observable<number> {
  const initialInterval = config.initialInterval < 0 ? 0 : config.initialInterval
  const maxInterval = config.maxInterval || Infinity
  const maxIterations = config.maxIterations || Infinity
  const backoffDelay = config.backoffDelay || exponentialBackoffDelay

  return of(0, scheduler).pipe(
    // Expand starts with number 1 and then recursively
    // projects each value to new Observable and puts it back in.
    expand(iteration =>
      iif(
        () => maxIterations === Infinity || iteration < maxIterations,
        timer(getDelay(backoffDelay(iteration, initialInterval), maxInterval)).pipe(mapTo(iteration + 1)),
        throwError(() => new IntervalMaxEmissionsReached()),
      ),
    ),
  )
}

/**
 * The configuration used to run the `intervalBackoff` function
 */
export interface IntervalBackoffConfig {
  /**
   * The initial interval, default = 0
   */
  initialInterval: number
  /**
   * Max interval between emissions, default = Infinity
   */
  maxInterval?: number
  /**
   * Maximum number of emissions, default = Infinity
   * When defined, the consumer is expected to specify a condition that finalizes the emissions,
   * using take(N), takeWhile, etc. Otherwise `IntervalMaxEmissionsReached` is thrown.
   */
  maxIterations?: number
  /**
   * An optional function to calculate the backoff delay, default = exponentialBackoffDelay
   */
  backoffDelay?: (iteration: number, initialInterval: number) => number
}

/**
 * Default exponential backoff delay
 */
function exponentialBackoffDelay(iteration: number, initialInterval: number): number {
  return Math.pow(2, iteration) * initialInterval
}

/**
 * Calculates the actual delay which can be limited by maxInterval
 */
function getDelay(backoffDelay: number, maxInterval: number): number {
  return Math.min(backoffDelay, maxInterval)
}
